/**
 * Server-side name/role lookups for the Platform logs (KAN-706).
 *
 * Super-admins cannot read tenant users' profiles from the client (and a deleted
 * person/company is gone from every list), so the `platform-resolve-people` edge
 * function names them at view time. It never returns an email.
 *
 * One cache per session (cleared at sign-out via clearPerUserDeviceState) and
 * in-flight dedupe, so every surface asking about the same id shares one call.
 * A failed call is NOT cached — the caller keeps showing a blank, not a guess.
 */
import { supabase } from '../supabase';

const CHUNK = 200;
const users = new Map(); // id -> { id, name, deleted, platform_role, roles_by_tenant }
const tenants = new Map(); // id -> { id, name, deleted }
const inflight = new Map(); // `u:id` / `t:id` -> Promise<void>
// Bumped at sign-out: an answer that lands after it belongs to the previous
// session and is dropped, never cached for the next person.
let generation = 0;

export function clearPlatformPeopleCache() {
  generation += 1;
  users.clear();
  tenants.clear();
  inflight.clear();
}

const chunks = (arr) => {
  const out = [];
  for (let i = 0; i < arr.length; i += CHUNK) out.push(arr.slice(i, i + CHUNK));
  return out;
};

async function fetchChunk(userIds, tenantIds) {
  const gen = generation;
  const { data, error } = await supabase.functions.invoke('platform-resolve-people', {
    body: { user_ids: userIds, tenant_ids: tenantIds },
  });
  if (error || !data?.success) throw new Error('platform-resolve-people failed');
  if (gen !== generation) return;
  (data.users || []).forEach((u) => users.set(u.id, u));
  (data.tenants || []).forEach((t) => tenants.set(t.id, t));
}

const snapshot = (userIds, tenantIds) => ({
  users: Object.fromEntries(userIds.filter((id) => users.has(id)).map((id) => [id, users.get(id)])),
  tenants: Object.fromEntries(tenantIds.filter((id) => tenants.has(id)).map((id) => [id, tenants.get(id)])),
});

/** Cache-only read (no network): what is already known for these ids. */
export function peekPlatformPeople({ userIds = [], tenantIds = [] } = {}) {
  return snapshot(userIds, tenantIds);
}

/**
 * @param {{ userIds?: string[], tenantIds?: string[] }} ids
 * @returns {Promise<{ users: Object, tenants: Object }>} id -> entry for every id the
 *   server answered. Never rejects: a failed call just leaves its ids out of the
 *   snapshot (and uncached) so the caller can retry once.
 */
export async function resolvePlatformPeople({ userIds = [], tenantIds = [] } = {}) {
  const uIds = [...new Set(userIds.filter(Boolean))];
  const tIds = [...new Set(tenantIds.filter(Boolean))];
  const needU = uIds.filter((id) => !users.has(id) && !inflight.has(`u:${id}`));
  const needT = tIds.filter((id) => !tenants.has(id) && !inflight.has(`t:${id}`));

  const jobs = [];
  const uChunks = chunks(needU);
  const tChunks = chunks(needT);
  for (let i = 0; i < Math.max(uChunks.length, tChunks.length); i += 1) {
    const u = uChunks[i] || [];
    const t = tChunks[i] || [];
    const job = fetchChunk(u, t).finally(() => {
      // Only remove our own entry: a sign-out may have let a newer job claim the key.
      u.forEach((id) => { if (inflight.get(`u:${id}`) === job) inflight.delete(`u:${id}`); });
      t.forEach((id) => { if (inflight.get(`t:${id}`) === job) inflight.delete(`t:${id}`); });
    });
    u.forEach((id) => inflight.set(`u:${id}`, job));
    t.forEach((id) => inflight.set(`t:${id}`, job));
    jobs.push(job);
  }
  // Ids another caller already has in flight: wait for that call too.
  const shared = [...uIds.map((id) => inflight.get(`u:${id}`)), ...tIds.map((id) => inflight.get(`t:${id}`))].filter(Boolean);
  await Promise.allSettled([...jobs, ...shared]);
  return snapshot(uIds, tIds);
}

/** True when every wanted id has an answer in `snap`. */
export function isFullyResolved(wanted, snap) {
  return wanted.userIds.every((id) => snap.users[id]) && wanted.tenantIds.every((id) => snap.tenants[id]);
}

/** Ids on these platform events that the known lookup maps cannot name. */
export function collectUnknownIds(events, { platformUsersByUserId = {}, tenantsById = {} } = {}) {
  const userIds = new Set();
  const tenantIds = new Set();
  (events || []).forEach((e) => {
    [e.actor_user_id, e.target_user_id].forEach((id) => {
      if (id && !platformUsersByUserId[id]) userIds.add(id);
    });
    if (e.target_tenant_id && !tenantsById[e.target_tenant_id]) tenantIds.add(e.target_tenant_id);
    // Ownership transfers carry owner ids; names are only a payload courtesy.
    const p = e.payload || {};
    [['old_owner_name', 'old_owner_user_id'], ['new_owner_name', 'new_owner_user_id']].forEach(([nameKey, idKey]) => {
      if (p[nameKey] == null && p[idKey] && !platformUsersByUserId[p[idKey]]) userIds.add(p[idKey]);
    });
  });
  return { userIds: [...userIds], tenantIds: [...tenantIds] };
}

/**
 * Fold resolver answers into the lookup maps the formatter reads. Ids still being
 * looked up are `pending` (blank), answered ids are `resolved`. Known entries win.
 */
export function mergeResolvedPeople({ platformUsersByUserId = {}, tenantsById = {} }, wanted, resolved) {
  const mergedUsers = { ...platformUsersByUserId };
  wanted.userIds.forEach((id) => {
    const r = resolved?.users?.[id];
    mergedUsers[id] = r
      ? {
        user_id: id,
        full_name: r.name,
        email: null,
        resolved: true,
        deleted: r.deleted,
        platform_role: r.platform_role ?? null,
        roles_by_tenant: r.roles_by_tenant || {},
      }
      : { user_id: id, pending: true };
  });
  const mergedTenants = { ...tenantsById };
  wanted.tenantIds.forEach((id) => {
    const r = resolved?.tenants?.[id];
    mergedTenants[id] = r
      ? { id, name: r.name, resolved: true, deleted: r.deleted }
      : { id, pending: true };
  });
  return { platformUsersByUserId: mergedUsers, tenantsById: mergedTenants };
}

/** Export path: resolve everything up front, then hand back the merged lookup maps. */
export async function namePeopleForEvents(events, known = {}) {
  const wanted = collectUnknownIds(events, known);
  // Unresolved ids export blank, never a guess.
  const resolved = await resolvePlatformPeople(wanted);
  return mergeResolvedPeople(known, wanted, resolved);
}
